import {
    Card,
    CardBody,
    Center,
    Fade,
    Flex,
    Grid,
    Spinner,
    Stack,
    Stat,
    StatArrow,
    StatHelpText,
    StatLabel,
    StatNumber,
    Text,
} from '@chakra-ui/react';
import { add, format, isBefore, startOfMonth } from 'date-fns';
import { last } from 'lodash';
import { useMemo, useState } from 'react';
import { CartesianGrid, Label, Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import { HistoricalStatistics } from '../../_openapi/portfolio-optimizer';
import { useHistoricalStatistics } from '../../api/portfolio';
import { tomorrow } from '../../utils/dateUtils';
import { DateCell } from '../../widgets/Table/DateCell';
import { MoneyCell } from '../../widgets/Table/MoneyCell';
import { NumberCell } from '../../widgets/Table/NumberCell';

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export const HistoricalStats = () => {
    const { data: history } = useHistoricalStatistics();
    const [hover, setHover] = useState(false);

    const current = useMemo(() => last(history), [history]);
    const startOfCurrentMonth = useMemo(() => {
        let date = startOfMonth(new Date());
        let value;

        while (!value && isBefore(date, tomorrow())) {
            const match = format(date, 'yyyy-MM-dd');
            value = history?.find(({ tradeDate }) => tradeDate === match);
            date = add(date, { days: 1 });
        }

        return value;
    }, [history]);

    if (!history) {
        return (
            <Center p={8}>
                <Spinner size="xl" />
            </Center>
        );
    }

    return (
        <Flex onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} gap={2}>
            <Card minHeight="320px" flex={1} maxWidth="50%" minWidth="600px">
                <CardBody p={3} position="relative">
                    <Summary
                        hover={hover}
                        label="AUM"
                        value={current?.aum}
                        referenceValue={startOfCurrentMonth?.aum}
                        formatter={(value) => currencyFormatter.format(value)}
                    />
                    <Chart data={history} hover={hover} dataKey="aum" label="AUM" />
                </CardBody>
            </Card>

            <Card minHeight="320px" flex={1} maxWidth="50%" minWidth="600px">
                <CardBody p={3} position="relative">
                    <Summary
                        hover={hover}
                        label="Number of Portfolios"
                        value={current?.portfoliosNr}
                        referenceValue={startOfCurrentMonth?.portfoliosNr}
                        formatter={(value) => String(value)}
                    />
                    <Chart data={history} hover={hover} dataKey="portfoliosNr" label="Number of Portfolios" />
                </CardBody>
            </Card>
        </Flex>
    );
};

const Summary = ({
    hover,
    label,
    value,
    referenceValue,
    formatter,
}: {
    hover: boolean;
    label: string;
    value: any;
    referenceValue: any;
    formatter: (value: number) => string;
}) => (
    <Fade in={!hover}>
        <Stat
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            background="blackAlpha.500"
            borderRadius="md"
            px={12}
            py={6}
            zIndex={1}
        >
            <StatLabel fontSize="xl">{label}</StatLabel>
            <>
                <StatNumber fontSize="5xl">{formatter(value)}</StatNumber>
                {referenceValue && value !== referenceValue && (
                    <StatHelpText>
                        <StatArrow type={value > referenceValue ? 'increase' : 'decrease'} />
                        {formatter(value - referenceValue)} this month
                    </StatHelpText>
                )}
            </>
        </Stat>
    </Fade>
);

const Chart = ({
    data,
    hover,
    dataKey,
    label,
}: {
    data: HistoricalStatistics[];
    hover: boolean;
    dataKey: string;
    label: string;
}) => (
    <ResponsiveContainer>
        <LineChart data={data} syncId="history" margin={{ bottom: 12 }}>
            <CartesianGrid strokeDasharray="4 4" stroke="#FFFFFF14" opacity={hover ? 1 : 0} />
            <XAxis
                minTickGap={14}
                tickMargin={6}
                dataKey="tradeDate"
                tick={{ fontSize: 10, fill: '#FFFFFFCC' }}
                tickFormatter={(dateStr: string) => format(new Date(dateStr), 'MM/dd/yy')}
                opacity={hover ? 1 : 0}
            >
                <Label offset={-2} value={label} position="bottom" fill="#FFFFFFCC" opacity={hover ? 1 : 0} />
            </XAxis>
            <Line dataKey={dataKey} stroke="#4FD1C5" dot={false} opacity={hover ? 1 : 0.25} />
            <Tooltip content={<CustomTooltip />} />
        </LineChart>
    </ResponsiveContainer>
);

const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <Stack background="gray.900" borderRadius="md" p={3} boxShadow="dark-lg">
                <Grid templateColumns="max-content max-content" columnGap={6}>
                    <DateCell dateString={payload[0].payload.tradeDate} />
                    <div>{/* grid placeholder */}</div>
                    <Text>Number of Portfolios</Text>
                    <NumberCell value={payload[0].payload.portfoliosNr} fractionDigits={0} />
                    <Text>Total NAV</Text>
                    <MoneyCell value={payload[0].payload.aum} fractionDigits={0} />
                </Grid>
            </Stack>
        );
    }

    return null;
};
